/**
 * Підбір nonce для доказу роботи.
 *
 * Умова сервера: hex-представлення `sha256(challenge + nonce)` починається з
 * `difficulty` нулів. Складність береться ВИКЛЮЧНО з відповіді сервера — поле
 * `difficulty` у тілі POST сервер свідомо ігнорує, бо колись воно бралося із
 * запиту і `difficulty: -1` обходив перевірку повністю.
 */

export interface SolveOptions {
  /** Стеля спроб. Без неї вкладка може крутити цикл нескінченно. */
  maxAttempts?: number;
  /** Стеля часу, мс. Спрацьовує раніше за maxAttempts на повільних пристроях. */
  maxDurationMs?: number;
  now?: () => number;
}

export type SolveResult =
  | { status: 'solved'; nonce: string; attempts: number }
  | { status: 'exhausted'; attempts: number; reason: 'attempts' | 'time' };

const DEFAULT_MAX_ATTEMPTS = 5_000_000;
const DEFAULT_MAX_DURATION_MS = 20_000;

function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let out = '';
  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, '0');
  }
  return out;
}

export async function solvePow(
  challenge: string,
  difficulty: number,
  options: SolveOptions = {},
): Promise<SolveResult> {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
    now = (): number => Date.now(),
  } = options;

  const encoder = new TextEncoder();
  const prefix = '0'.repeat(Math.max(0, difficulty));
  const startedAt = now();

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const nonce = String(attempt);
    const digest = await crypto.subtle.digest('SHA-256', encoder.encode(challenge + nonce));
    if (toHex(digest).startsWith(prefix)) {
      return { status: 'solved', nonce, attempts: attempt + 1 };
    }
    // Час перевіряємо рідко: виклик годинника на кожній ітерації коштує
    // помітно дорожче за сам хеш.
    if ((attempt & 0x3ff) === 0x3ff && now() - startedAt > maxDurationMs) {
      return { status: 'exhausted', attempts: attempt + 1, reason: 'time' };
    }
  }

  return { status: 'exhausted', attempts: maxAttempts, reason: 'attempts' };
}
